// The node page's keyboard shortcuts (WEB-36). Pure: the page applies the
// action. Not while typing in a field, not with a modifier, not while a
// dialog or drawer is open (they have their own keys: Esc closes).
import type { Config } from './api'
import type { Preset, Selection } from './store'

export type Shortcut =
    { do: 'window'; preset: Preset } | { do: 'select'; sel: Selection } | { do: 'edit' } | { do: 'help' }

// the window presets by key: 0 for ten minutes, then the hours' first digit
const windows: Record<string, Preset> = { '0': '10m', '1': '1h', '6': '6h', '2': '24h', '7': '7d' }

export const SHORTCUTS: [string, string][] = [
    ['0 1 6 2 7', 'time window: 10 min, 1 h, 6 h, 24 h, 7 days'],
    ['[ ]', 'the previous or next input, encode, destination or output'],
    ['e', 'edit the selected one'],
    ['?', 'these shortcuts'],
    ['Esc', 'close the dialog or drawer'],
]

// the entities in the list's order: inputs, encodes, destinations, outputs
export function entityOrder(cfg: Config): Selection[] {
    return [
        ...cfg.inputs.map((x) => ({ kind: 'input' as const, id: x.id })),
        ...cfg.encodes.map((x) => ({ kind: 'encode' as const, id: x.id })),
        ...cfg.destinations.map((x) => ({ kind: 'destination' as const, id: x.id })),
        ...(cfg.outputs ?? []).map((x) => ({ kind: 'output' as const, id: x.id })),
    ]
}

// shortcut is what a key asks for, or null. typing: focus is in a field;
// modal: a dialog or drawer is open.
export function shortcut(
    e: { key: string; ctrlKey: boolean; metaKey: boolean; altKey: boolean },
    cfg: Config | null,
    sel: Selection | null,
    typing: boolean,
    modal: boolean,
): Shortcut | null {
    if (typing || modal || e.ctrlKey || e.metaKey || e.altKey) return null
    const preset = windows[e.key]
    if (preset) return { do: 'window', preset }
    if (e.key === '?') return { do: 'help' }
    if (e.key === 'e' && sel && sel.kind !== 'machine') return { do: 'edit' }
    if ((e.key === '[' || e.key === ']') && cfg) {
        const list = entityOrder(cfg)
        if (!list.length) return null
        const at = sel ? list.findIndex((x) => x.kind === sel.kind && x.id === sel.id) : -1
        const next = e.key === ']' ? (at + 1) % list.length : at <= 0 ? list.length - 1 : at - 1
        return { do: 'select', sel: list[next]! }
    }
    return null
}
